// @ts-nocheck
import { createEffect, onMount, onCleanup } from "solid-js";
import * as d3 from "d3";
import { streamingStore } from "../../../../store/streamingStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { selectedTime, isPlaying, smoothPlaybackTimeForBoat, timeWindow, getLiveDisplayTargetMs } from "../../../../store/playbackStore";
import { persistantStore } from "../../../../store/persistantStore";
import { defaultChannelsStore } from "../../../../store/defaultChannelsStore";
import {
  getInterpolatedPointAtTime,
  normalizeRotationDeg,
  shortestPathRotation,
  smoothHeadingDeg,
} from "../../../../utils/trackInterpolation";
import { debug, warn } from "../../../../utils/console";
import { resolveBoatMarkerStyle } from "../renderers/boatMarkerStyle";
import { computeScreenWindArrow, ensureSvgArrowheadMarker } from "../renderers/windArrowGeometry";
import { liveReplayFilteredDataOptions } from "../../../../utils/liveReplayFilteredData";
import type { LiveReplaySourceStyle } from "../../../../utils/liveReplayFramePoints";

export interface LiveMultiBoatLayerProps {
  map: any;
  mapContainer: HTMLElement;
  samplingFrequency: number;
  selectedSourceIds: Set<number>;
  onBoatClick?: (p: any) => void;
  /** When true, passed for consistency with LiveTrackLayer; boat positions are not throttled so they stay in sync with tracks */
  inSplitView?: boolean;
  /** When true, boats are display-only (no click-to-seek). */
  replayMode?: boolean;
  /** Replay live map: shared per-source interpolated point from LiveMapContainer. */
  framePoints?: Map<number, unknown>;
  /** Replay live map: memoized sourceId → name/color. */
  sourceStyles?: Map<number, LiveReplaySourceStyle>;
  /** Rolling window minutes (ignores cross-window timeWindow=0 sync). */
  replayTimeWindowMin?: number;
  /** Averaged wind per boat name; present only while the wind overlay wants boat wind drawn. */
  boatWindByName?: Map<string, { tws: number; twd: number }>;
}

// Track state per source: last position, last timestamp, boat icon
interface SourceState {
  lastPoint: any | null;
  lastTimestamp: number;
  boatIcon: d3.Selection<SVGGElement, unknown, null, undefined> | null;
  boatCreated: boolean;
  hasLoggedInvalidCoords?: boolean;
  lastRejectedJumpAt?: number;
  /** Smoothed compass heading (0–360) for shortest arc across north. */
  lastHdgDeg?: number;
  /** Last applied SVG rotate(deg) — do not parse from mid-transition transform. */
  lastScreenRotation?: number;
}

export default function LiveMultiBoatLayer(props: LiveMultiBoatLayerProps) {
  // Track state per source
  const sourceStates = new Map<number, SourceState>();

  /** Remove boat overlays for sources turned off in map settings */
  createEffect(() => {
    const selected = props.selectedSourceIds || new Set<number>();
    const key = Array.from(selected)
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => a - b)
      .join(",");
    void key;
    for (const [sourceId, state] of [...sourceStates.entries()]) {
      if (!selected.has(sourceId)) {
        if (state.boatIcon) {
          state.boatIcon.remove();
        }
        removeBoatWindArrow(sourceId);
        sourceStates.delete(sourceId);
      }
    }
  });

  // Helper to get timestamp from data point
  const getTimestamp = (d: any): number => {
    if (!d) return 0;
    const timestamp = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  // Helper to get latest point from WebSocket data array
  const getLatestPoint = (points: any[]): any | null => {
    if (!points || points.length === 0) return null;
    const sorted = [...points].sort((a, b) => getTimestamp(b) - getTimestamp(a));
    return sorted[0] || null;
  };

  const readFiniteNumber = (value: unknown): number | undefined => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") return undefined;
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  // Getters for interpolation (live data shape: Lat_dd/Lng_dd/Hdg_deg or channel names)
  const getLatLive = (d: any): number | undefined => {
    if (!d) return undefined;
    const name = defaultChannelsStore.latName() || "Lat_dd";
    const v = d[name] ?? d.lat_dd ?? d.Lat_dd ?? d.Lat ?? d.lat;
    return readFiniteNumber(v);
  };
  const getLngLive = (d: any): number | undefined => {
    if (!d) return undefined;
    const name = defaultChannelsStore.lngName() || "Lng_dd";
    const v = d[name] ?? d.lng_dd ?? d.Lng_dd ?? d.Lng ?? d.lng;
    return readFiniteNumber(v);
  };
  const getHdgLive = (d: any): number | undefined => {
    if (!d) return undefined;
    const name = defaultChannelsStore.hdgName() || "Hdg_deg";
    const v = d[name] ?? d.hdg_deg ?? d.Hdg_deg ?? d.Hdg ?? d.hdg;
    return readFiniteNumber(v);
  };

  const getCoords = (point: any): { lat: number; lng: number } | null => {
    if (!point) return null;
    const latFieldName = defaultChannelsStore.latName() || "Lat_dd";
    const lngFieldName = defaultChannelsStore.lngName() || "Lng_dd";
    const lat = readFiniteNumber(point[latFieldName] ?? point.lat_dd ?? point.Lat_dd ?? point.Lat ?? point.lat);
    const lng = readFiniteNumber(point[lngFieldName] ?? point.lng_dd ?? point.Lng_dd ?? point.Lng ?? point.lng);
    if (lat === undefined || lng === undefined) return null;
    return { lat, lng };
  };

  const distanceMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
    const radiusM = 6371000;
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * radiusM * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  const shouldRenderPlayingPoint = (sourceId: number, state: SourceState, point: any, pointTimestamp: number): boolean => {
    if (pointTimestamp === 0 || pointTimestamp <= state.lastTimestamp) return false;
    if (!state.lastPoint || state.lastTimestamp <= 0) return true;

    const lastCoords = getCoords(state.lastPoint);
    const nextCoords = getCoords(point);
    if (!lastCoords || !nextCoords) return true;

    const elapsedSec = Math.max(0.001, (pointTimestamp - state.lastTimestamp) / 1000);
    const distance = distanceMeters(lastCoords, nextCoords);
    // 150 kph is already above expected GP50 boat speed; margin handles GPS jitter and low dt frames.
    const maxDistance = 30 + (150 / 3.6) * elapsedSec;
    if (distance <= maxDistance) return true;

    const now = Date.now();
    if (!state.lastRejectedJumpAt || now - state.lastRejectedJumpAt > 5000) {
      state.lastRejectedJumpAt = now;
      debug("[LiveMultiBoatLayer] Rejected implausible live boat jump", {
        sourceId,
        distanceMeters: Math.round(distance),
        elapsedMs: Math.round(elapsedSec * 1000),
        maxDistanceMeters: Math.round(maxDistance),
        lastTimestamp: state.lastTimestamp,
        pointTimestamp,
      });
    }
    return false;
  };

  // Calculate boat size based on zoom level
  /** Attach boat SVG on the Mapbox canvas container (same as LiveTrackLayer / useBoatAnimation). */
  const resolveBoatOverlayContainer = (): HTMLElement | null => {
    if (props.map) {
      try {
        const canvasContainer = props.map.getCanvasContainer?.() as HTMLElement | undefined;
        if (canvasContainer) return canvasContainer;
      } catch {
        /* map not ready */
      }
    }
    return props.mapContainer ?? null;
  };

  const calculateBoatSize = (map: any): number => {
    if (!map) return 1;
    const zoom = map.getZoom();
    const center = map.getCenter();
    const latitude = center ? center.lat : 0;
    const metersPerPixel = 156543.03392 * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom);
    const targetMeters = 22;
    const targetPixels = targetMeters / metersPerPixel;
    const baseSize = 24;
    const realAspectRatioScale = targetPixels / baseSize;
    const defaultScale = 0.75;
    let finalScale = Math.max(defaultScale, realAspectRatioScale);
    const absoluteMaxScale = 12.0;
    finalScale = Math.min(absoluteMaxScale, finalScale);
    
    // Apply 25% size reduction for multihull boats (multiply by 0.75)
    const { selectedClassIcon } = persistantStore;
    const iconType = selectedClassIcon()?.toLowerCase() || 'monohull';
    if (iconType === 'multihull') {
      finalScale = finalScale * 0.75;
    }
    
    return finalScale;
  };

  // Create boat icon for a source
  const createBoatIcon = (sourceId: number, color: string, sourceName?: string): d3.Selection<SVGGElement, unknown, null, undefined> | null => {
    const overlayContainer = resolveBoatOverlayContainer();
    if (!overlayContainer) return null;

    // Check if boat already exists
    const existingBoat = d3.select(overlayContainer).select(`#live-boat-${sourceId}`);
    if (!existingBoat.empty()) {
      return existingBoat.node() as SVGGElement ? d3.select(existingBoat.node() as SVGGElement) : null;
    }

    const mapRect = overlayContainer.getBoundingClientRect();
    const width = mapRect.width || overlayContainer.clientWidth || 800;
    const height = mapRect.height || overlayContainer.clientHeight || 600;

    // Create SVG overlay if it doesn't exist
    let svgOverlay = d3.select(overlayContainer).select('.boat-overlay-svg');
    if (svgOverlay.empty()) {
      svgOverlay = d3.select(overlayContainer)
        .append('svg')
        .attr('class', 'boat-overlay-svg')
        .attr('width', width)
        .attr('height', height)
        .style('position', 'absolute')
        .style('top', '0')
        .style('left', '0')
        .style('width', '100%')
        .style('height', '100%')
        .style('pointer-events', 'none')
        .style('z-index', '1000');
    }

    // Create boat group
    const boatGroup = svgOverlay
      .append('g')
      .attr('id', `live-boat-${sourceId}`)
      .attr('class', 'live-boat-icon');

    // Get boat path based on class icon type
    const { selectedClassIcon } = persistantStore;
    const iconType = selectedClassIcon()?.toLowerCase() || 'monohull';
    let boatPath: string;
    if (iconType === 'multihull') {
      // Multihull coordinates scaled to be 2x wider than monohull, centered at (0,0)
      boatPath = 'M0 12 L5.332 0 L5.332 6 L6.665 12 L8 6 L8 -12 L-8 -12 L-8 6 L-6.665 12 L-5.332 6 L-5.332 0 Z';
    } else {
      // Default monohull path
      boatPath = 'M0 -12 L-4 -12 L-4 0 L-2 8 L0 12 L2 8 L4 0 L4 -12 Z';
    }

    // Boat path (NZL is forced to a solid white marker so it's visible on the dark basemap)
    const boatMarkerStyle = resolveBoatMarkerStyle(sourceName, color, '#000');
    boatGroup
      .append('path')
      .attr('d', boatPath)
      .attr('fill', boatMarkerStyle.fill)
      .attr('stroke', boatMarkerStyle.stroke)
      .attr('stroke-width', '1')
      .style('opacity', 1);

    // Add source name label (11px white text, positioned to the right of boat)
    // Label rotates with boat, but flips 180° when upside down for readability
    boatGroup.append("text")
      .attr("class", "boat-source-name")
      .attr("x", 12) // Position to the right of boat
      .attr("y", 0) // Center
      .attr("font-size", "11px")
      .attr("fill", "white")
      .attr("stroke", "black")
      .attr("stroke-width", "0.5px")
      .attr("stroke-opacity", "0.8")
      .attr("paint-order", "stroke")
      .attr("transform", "rotate(0)") // Will be updated to flip when upside down
      .style("pointer-events", "none")
      .text(sourceName || "");

    // Add click handler if provided (disabled in replay mode)
    if (props.onBoatClick && !props.replayMode) {
      boatGroup
        .style('pointer-events', 'all')
        .style('cursor', 'pointer')
        .on('click', (event) => {
          const state = sourceStates.get(sourceId);
          // Use current point (lastPoint is the most recent position)
          if (state && state.lastPoint) {
            props.onBoatClick?.(state.lastPoint);
          }
        });
    }

    debug(`[LiveMultiBoatLayer] Created boat icon for source ${sourceId}`);
    return boatGroup;
  };

  /** Boat name/color, from the memoized replay styles when present, else the sources store. */
  const resolveNameColor = (sourceId: number): { name: string | undefined; color: string } => {
    const styled = props.sourceStyles?.get(sourceId);
    const name = styled?.sourceName ?? sourcesStore.getSourceName(sourceId);
    return { name, color: styled?.color || (name ? sourcesStore.getSourceColor(name) : "") || "#1f77b4" };
  };

  /**
   * Wind arrow + TWS label for one boat, matching the per-mark arrows.
   *
   * It lives in its OWN group beside the boat icon, not inside it: the boat group carries a
   * translate/scale/rotate transform, and the arrow must stay a fixed 20px, unrotated by the hull's heading and
   * locked to the map bearing instead. The arrowhead marker is defined in this layer's own <defs> — the mark
   * layer's markers live in a different <svg> that disappears with the "Marks & Boundaries" toggle.
   */
  const updateBoatWindArrow = (sourceId: number, x: number, y: number, mapBearing: number) => {
    const overlayContainer = resolveBoatOverlayContainer();
    if (!overlayContainer) return;
    const svgOverlay = d3.select(overlayContainer).select(".boat-overlay-svg");
    if (svgOverlay.empty()) return;

    const { name, color } = resolveNameColor(sourceId);
    const wind = name ? props.boatWindByName?.get(name) : undefined;
    const groupId = `live-boat-wind-${sourceId}`;
    let group = svgOverlay.select(`#${groupId}`);

    if (!wind || !Number.isFinite(wind.twd) || !Number.isFinite(wind.tws)) {
      if (!group.empty()) group.remove();
      return;
    }

    const arrowColor = resolveBoatMarkerStyle(name, color, "#000").fill;
    const markerUrl = ensureSvgArrowheadMarker(svgOverlay, `live-boat-wind-arrowhead-${sourceId}`, arrowColor);
    const { tipX, tipY, labelX, labelY } = computeScreenWindArrow({ x, y, twd: wind.twd, mapBearing });

    if (group.empty()) {
      group = svgOverlay.append("g").attr("id", groupId).attr("class", "live-boat-wind").style("pointer-events", "none");
      group.append("line").attr("class", "live-boat-wind-arrow").attr("stroke-width", 1.5);
      group
        .append("text")
        .attr("class", "live-boat-wind-label")
        .attr("font-size", "8px")
        .attr("font-family", "Arial, sans-serif")
        .attr("stroke", "black")
        .attr("stroke-width", "0.5px")
        .attr("stroke-opacity", "0.8")
        .attr("paint-order", "stroke");
    }

    group
      .select("line.live-boat-wind-arrow")
      .attr("x1", x)
      .attr("y1", y)
      .attr("x2", tipX)
      .attr("y2", tipY)
      .attr("stroke", arrowColor)
      .attr("marker-end", markerUrl);
    group
      .select("text.live-boat-wind-label")
      .attr("x", labelX)
      .attr("y", labelY)
      .attr("fill", arrowColor)
      .text(wind.tws.toFixed(1));
  };

  /** Drop a boat's wind arrow (deselected, or the layer is tearing down). */
  const removeBoatWindArrow = (sourceId: number) => {
    const overlayContainer = resolveBoatOverlayContainer();
    if (!overlayContainer) return;
    d3.select(overlayContainer).select(`#live-boat-wind-${sourceId}`).remove();
  };

  // Helper function to calculate label flip rotation (180° when upside down for readability)
  const calculateLabelFlip = (boatRotation: number): number => {
    // Normalize rotation to 0-360 range
    const normalizedRotation = ((boatRotation % 360) + 360) % 360;
    // Flip 180° when text would be upside down (between 90° and 270°)
    if (normalizedRotation > 90 && normalizedRotation < 270) {
      return 180;
    }
    return 0;
  };

  // Update boat position immediately (no transition)
  const updateBoatPosition = (sourceId: number, point: any) => {
    const state = sourceStates.get(sourceId);
    if (!state || !state.boatIcon || !props.map) return;

    // Use default channel names (Lat_dd, Lng_dd) - processor now outputs these directly
    const latFieldName = defaultChannelsStore.latName() || 'Lat_dd';
    const lngFieldName = defaultChannelsStore.lngName() || 'Lng_dd';
    const lng = point[lngFieldName] ?? point.lng_dd ?? point.Lng_dd;
    const lat = point[latFieldName] ?? point.lat_dd ?? point.Lat_dd;
    
    // Check if coordinates are valid (not null, not undefined, and finite)
    // Null values are expected when GPS data is temporarily unavailable
    if (lng === null || lat === null || lng === undefined || lat === undefined || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      // Only log warning if coordinates are undefined (missing field) or invalid number
      // Don't warn for null values - these are expected when GPS is temporarily unavailable
      if (lng !== null && lat !== null) {
        // Only warn if the field exists but has an invalid value (not null)
        const hasLatField = latFieldName in point || 'Lat_dd' in point || 'lat_dd' in point;
        const hasLngField = lngFieldName in point || 'Lng_dd' in point || 'lng_dd' in point;
        
        if (hasLatField && hasLngField) {
          // Field exists but value is invalid - this is unexpected, log once per source
          if (!state.hasLoggedInvalidCoords) {
            warn(`[LiveMultiBoatLayer] Invalid coordinate values for source ${sourceId} (will not warn again):`, { 
              lng, 
              lat,
              latFieldName,
              lngFieldName
            });
            state.hasLoggedInvalidCoords = true;
          }
        }
      }
      // Silently skip points with null/undefined/invalid coordinates
      return;
    }

    const mapPoint = props.map.project([lng, lat]);
    if (!mapPoint || isNaN(mapPoint.x) || isNaN(mapPoint.y)) {
      warn(`[LiveMultiBoatLayer] Invalid projected point for source ${sourceId}`);
      return;
    }

    const mapBearing = props.map.getBearing ? props.map.getBearing() : 0;
    const hdgFieldName = defaultChannelsStore.hdgName() || 'Hdg_deg';
    const rawHdg = point[hdgFieldName] ?? point.hdg_deg ?? point.Hdg_deg ?? point.Hdg ?? point.hdg;
    const boatScale = calculateBoatSize(props.map);

    state.boatIcon.interrupt();

    let finalRotation = state.lastScreenRotation ?? 0;
    if (rawHdg !== undefined && rawHdg !== null && !isNaN(rawHdg) && Number.isFinite(Number(rawHdg))) {
      const smoothHdg = smoothHeadingDeg(state.lastHdgDeg, Number(rawHdg));
      state.lastHdgDeg = smoothHdg;
      const targetRotation = normalizeRotationDeg(smoothHdg + 180 - mapBearing);
      finalRotation = shortestPathRotation(state.lastScreenRotation ?? targetRotation, targetRotation);
      state.lastScreenRotation = finalRotation;
    } else if (state.lastHdgDeg != null) {
      const targetRotation = normalizeRotationDeg(state.lastHdgDeg + 180 - mapBearing);
      finalRotation = shortestPathRotation(state.lastScreenRotation ?? targetRotation, targetRotation);
      state.lastScreenRotation = finalRotation;
    }

    const currentTransform = state.boatIcon.attr("transform");
    const currentX = parseFloat(currentTransform?.match(/translate\(([^,]+)/)?.[1] ?? String(mapPoint.x));
    const currentY = parseFloat(currentTransform?.match(/translate\([^,]+,([^)]+)\)/)?.[1] ?? String(mapPoint.y));
    const currentScale = parseFloat(currentTransform?.match(/scale\(([^)]+)\)/)?.[1] ?? String(boatScale));

    state.boatIcon
      .attr(
        "transform",
        `translate(${currentX}, ${currentY}) scale(${currentScale}) rotate(${finalRotation})`
      )
      .transition()
      .duration(200)
      .ease(d3.easeLinear)
      .attr("transform", `translate(${mapPoint.x}, ${mapPoint.y}) scale(${boatScale}) rotate(${finalRotation})`);

    const sourceNameText = state.boatIcon.select(".boat-source-name");
    if (!sourceNameText.empty()) {
      const flipRotation = calculateLabelFlip(finalRotation);
      sourceNameText.attr("transform", `rotate(${flipRotation})`);
    }

    updateBoatWindArrow(sourceId, mapPoint.x, mapPoint.y, mapBearing);
  };

  const shouldRenderInterpolatedBoat = (
    sourceId: number,
    state: SourceState,
    point: any,
    playheadMs: number
  ): boolean => {
    if (playheadMs <= 0 || playheadMs <= state.lastTimestamp) return false;
    if (!state.lastPoint || state.lastTimestamp <= 0) return true;

    const lastCoords = getCoords(state.lastPoint);
    const nextCoords = getCoords(point);
    if (!lastCoords || !nextCoords) return true;

    // Plausibility must be judged against the elapsed time between the two REAL samples, not the
    // playhead tick. Replay frame points are raw samples (buildLiveReplayFramePoints does not
    // interpolate), so in replay/processing the catalog is sparse and the boat legitimately steps
    // one sample-gap (often 1-3 s, tens of metres) at a time. The playhead advances only ~200 ms
    // per boat frame and each rejection below resets state.lastTimestamp to the playhead, so a
    // playhead-based budget can never grow to cover a real sample gap — the boat freezes forever
    // (tracks keep drawing because they have no such gate). Fall back to the playhead delta only
    // when the sample timestamps are unusable.
    const sampleDeltaMs = getTimestamp(point) - getTimestamp(state.lastPoint);
    const elapsedMs = sampleDeltaMs > 0 ? sampleDeltaMs : playheadMs - state.lastTimestamp;
    const elapsedSec = Math.max(0.001, elapsedMs / 1000);
    const distance = distanceMeters(lastCoords, nextCoords);
    const maxDistance = 30 + (150 / 3.6) * elapsedSec;
    if (distance <= maxDistance) return true;

    const now = Date.now();
    if (!state.lastRejectedJumpAt || now - state.lastRejectedJumpAt > 5000) {
      state.lastRejectedJumpAt = now;
      debug("[LiveMultiBoatLayer] Rejected implausible interpolated boat jump", {
        sourceId,
        distanceMeters: Math.round(distance),
        elapsedMs: Math.round(elapsedSec * 1000),
        maxDistanceMeters: Math.round(maxDistance),
        lastTimestamp: state.lastTimestamp,
        playheadMs,
      });
    }
    // Advance playhead clock without moving the icon — avoids widening the speed gate until a teleport.
    state.lastTimestamp = playheadMs;
    return false;
  };

  const resolveBoatStyle = (
    sourceId: number,
    point: any,
    styles?: Map<number, LiveReplaySourceStyle>
  ): { sourceName: string; color: string } => {
    const styled = styles?.get(sourceId);
    const pointSourceName = point?.source_name;
    const sources = sourcesStore.sources();
    let sourceInfo: any = null;
    if (pointSourceName) {
      sourceInfo = sources.find((s: any) =>
        s.source_name && String(s.source_name).toLowerCase() === String(pointSourceName).toLowerCase()
      );
    }
    if (!sourceInfo) {
      sourceInfo = sources.find((s: any) => Number(s.source_id) === Number(sourceId));
    }
    const sourceName = styled?.sourceName || sourceInfo?.source_name || pointSourceName || `Source ${sourceId}`;
    const color = styled?.color || sourceInfo?.color || sourcesStore.getSourceColor(sourceName) || "#1f77b4";
    return { sourceName, color };
  };

  const ensureBoatIcon = (
    sourceId: number,
    point: any,
    state: SourceState,
    styles?: Map<number, LiveReplaySourceStyle>
  ): void => {
    if (state.boatCreated || !props.map || !resolveBoatOverlayContainer()) return;
    const { sourceName, color } = resolveBoatStyle(sourceId, point, styles);
    state.boatIcon = createBoatIcon(sourceId, color, sourceName);
    state.boatCreated = true;
  };

  const applyBoatPoint = (
    sourceId: number,
    state: SourceState,
    point: any,
    pointTimestamp: number
  ): void => {
    state.lastPoint = point;
    state.lastTimestamp = pointTimestamp;
    if (state.boatIcon && props.map) {
      updateBoatPosition(sourceId, point);
    }
  };

  const boatInterpolateOptions = {
    getTimestamp: (d: any) => new Date(getTimestamp(d)),
    getLat: getLatLive,
    getLng: getLngLive,
    getHdg: getHdgLive,
    assumeSorted: true,
    noExtrapolate: true,
  };

  // Watch for WebSocket data updates and selectedTime changes
  createEffect(() => {
    const newDataMap = streamingStore.getNewData()();
    const currentTime = selectedTime();
    const currentlyPlaying = isPlaying();
    const selected = props.selectedSourceIds || new Set<number>();

    // When paused, position boats at selectedTime
    if (!currentlyPlaying && currentTime instanceof Date) {
      const currentTimeMs = currentTime.getTime();
      
      // Get all filtered data from streamingStore
      const filteredDataMap = streamingStore.getFilteredData(
        selected,
        props.replayMode ? liveReplayFilteredDataOptions() : undefined
      );
      
      for (const sourceId of selected) {
        const sourceData = filteredDataMap.get(sourceId) || [];
        if (sourceData.length === 0) continue;
        
        // Find the closest point to selectedTime
        let closestPoint: any = null;
        let closestDelta = Infinity;
        
        for (const point of sourceData) {
          const ts = getTimestamp(point);
          if (ts === 0) continue;
          
          const delta = Math.abs(ts - currentTimeMs);
          if (delta < closestDelta) {
            closestDelta = delta;
            closestPoint = point;
          }
        }
        
        // Only update if we found a point within reasonable tolerance (5 seconds)
        if (closestPoint && closestDelta <= 5000) {
          // Get or create state for this source
          let state = sourceStates.get(sourceId);
          if (!state) {
            state = {
              lastPoint: null,
              lastTimestamp: 0,
              boatIcon: null,
              boatCreated: false
            };
            sourceStates.set(sourceId, state);
          }
          
          // Create boat icon if needed
          if (!state.boatCreated && props.map && resolveBoatOverlayContainer()) {
            const { sourceName, color } = resolveBoatStyle(sourceId, closestPoint, props.sourceStyles);
            state.boatIcon = createBoatIcon(sourceId, color, sourceName);
            state.boatCreated = true;
          }
          
          // Update boat position to the point at selectedTime
          if (state.boatIcon && props.map) {
            state.lastPoint = closestPoint;
            state.lastTimestamp = getTimestamp(closestPoint);
            updateBoatPosition(sourceId, closestPoint);
          }
        }
      }
      return; // Don't process websocket updates when paused
    }

    // Replay live map with rolling window: boat position from interpolated playhead (~5fps), not raw WS jumps.
    const replayInterpolatedLane =
      props.replayMode && currentlyPlaying && (props.replayTimeWindowMin ?? 0) > 0;
    if (replayInterpolatedLane) {
      return;
    }

    const replayWsLane = props.replayMode && currentlyPlaying;
    const legacyWsLane = !props.replayMode && currentlyPlaying && timeWindow() <= 0;
    if (!replayWsLane && !legacyWsLane) {
      return;
    }

    if (!newDataMap) return;

    const playheadCapMs = replayWsLane ? getLiveDisplayTargetMs(selected) : Number.POSITIVE_INFINITY;

    for (const [sourceId, newPoints] of newDataMap.entries()) {
      if (!selected.has(sourceId)) {
        continue;
      }

      if (!newPoints || newPoints.length === 0) {
        continue;
      }

      // Get the latest point (should be only 1 point from server, but be safe)
      // MUST be defined before boat icon creation to use in color lookup fallback
      const latestPoint = getLatestPoint(newPoints);
      
      if (!latestPoint) {
        continue;
      }

      let state = sourceStates.get(sourceId);
      if (!state) {
        state = {
          lastPoint: null,
          lastTimestamp: 0,
          boatIcon: null,
          boatCreated: false
        };
        sourceStates.set(sourceId, state);
      }

      ensureBoatIcon(sourceId, latestPoint, state, props.sourceStyles);

      const latestTimestamp = getTimestamp(latestPoint);
      if (latestTimestamp === 0) {
        continue;
      }
      if (replayWsLane && latestTimestamp > playheadCapMs) {
        continue;
      }

      if (shouldRenderPlayingPoint(sourceId, state, latestPoint, latestTimestamp)) {
        applyBoatPoint(sourceId, state, latestPoint, latestTimestamp);
      }
    }

    if (!legacyWsLane) {
      return;
    }

    // Legacy path: clear processed WS batches after other layers read them
    // Use setTimeout to ensure all components have processed the data first
    setTimeout(() => {
      const currentData = streamingStore.getNewData()();
      if (currentData && currentData.size > 0) {
        const sourcesToClear = new Set<number>();
        for (const sourceId of selected) {
          if (currentData.has(sourceId)) {
            sourcesToClear.add(sourceId);
          }
        }
        if (sourcesToClear.size > 0) {
          streamingStore.clearProcessedData(sourcesToClear);
        }
      }
    }, 100); // Small delay to ensure all components processed
  });

  // Smooth boat motion: interpolate between catalog samples at ~5fps boat playhead
  createEffect(() => {
    const replayWindowMin = props.replayTimeWindowMin ?? 0;
    const replaySmooth = props.replayMode && replayWindowMin > 0;
    const legacySmooth = !props.replayMode && timeWindow() > 0;
    if (!isPlaying() || (!replaySmooth && !legacySmooth)) return;
    if (!props.map || !resolveBoatOverlayContainer()) return;

    const smoothTime = smoothPlaybackTimeForBoat();
    const selected = props.selectedSourceIds || new Set<number>();
    void streamingStore.getLiveDataAppendVersion()();
    void streamingStore.getHistoricalDataVersion()();
    const framePoints = props.replayMode ? props.framePoints : undefined;
    void framePoints;

    const playheadMs = smoothTime instanceof Date ? smoothTime.getTime() : 0;
    if (playheadMs <= 0) return;

    const liveCatalogMap = replaySmooth && !framePoints?.size
      ? streamingStore.getLiveRenderData(selected, { shallow: true })
      : null;

    for (const sourceId of selected) {
      let point: any | null = null;

      if (replaySmooth) {
        point = (framePoints?.get(sourceId) as any) ?? null;
        if (!point) {
          const catalog = liveCatalogMap?.get(sourceId) ?? [];
          if (catalog.length === 0) continue;
          point = getInterpolatedPointAtTime(catalog, smoothTime, boatInterpolateOptions);
        }
      } else {
        const catalog =
          streamingStore.getFilteredData(selected, { effectivePlaybackTime: smoothTime }).get(sourceId) ?? [];
        if (catalog.length === 0) continue;
        point = getInterpolatedPointAtTime(catalog, smoothTime, boatInterpolateOptions);
      }

      if (!point) continue;

      let state = sourceStates.get(sourceId);
      if (!state) {
        state = { lastPoint: null, lastTimestamp: 0, boatIcon: null, boatCreated: false };
        sourceStates.set(sourceId, state);
      }

      ensureBoatIcon(sourceId, point, state, props.sourceStyles);

      if (replaySmooth) {
        if (!shouldRenderInterpolatedBoat(sourceId, state, point, playheadMs)) continue;
        applyBoatPoint(sourceId, state, point, playheadMs);
      } else {
        const pointTimestamp = getTimestamp(point);
        if (!shouldRenderPlayingPoint(sourceId, state, point, pointTimestamp)) continue;
        applyBoatPoint(sourceId, state, point, pointTimestamp);
      }
    }
  });

  // Handle map movements to update boat positions
  createEffect(() => {
    if (!props.map) return;

    const updateBoatPositions = () => {
      for (const [sourceId, state] of sourceStates.entries()) {
        if (state.lastPoint && state.boatIcon) {
          // Re-project position on map movement
          updateBoatPosition(sourceId, state.lastPoint);
        }
      }
    };

    // Captured locally so teardown detaches from the SAME map instance we attached to; the old
    // disposer read props.map at teardown time, which breaks once the prop has changed or gone null.
    const attachedMap = props.map;
    attachedMap.on('render', updateBoatPositions);
    attachedMap.on('moveend', updateBoatPositions);
    attachedMap.on('zoomend', updateBoatPositions);
    attachedMap.on('rotateend', updateBoatPositions);

    // onCleanup, not a returned function: Solid discards createEffect return values, so each re-run
    // used to attach another four handlers to the map and detach none.
    onCleanup(() => {
      attachedMap.off('render', updateBoatPositions);
      attachedMap.off('moveend', updateBoatPositions);
      attachedMap.off('zoomend', updateBoatPositions);
      attachedMap.off('rotateend', updateBoatPositions);
    });
  });

  // Boat wind updates on its own 1 Hz cadence. A still map fires no 'render', so refresh the arrows here too —
  // this is what makes toggling Boat Wind off clear them immediately instead of at the next map interaction.
  createEffect(() => {
    void props.boatWindByName;
    if (!props.map) return;
    for (const [sourceId, state] of sourceStates.entries()) {
      if (state.lastPoint && state.boatIcon) updateBoatPosition(sourceId, state.lastPoint);
    }
  });

  // Cleanup on unmount
  onCleanup(() => {
    // Remove all boat icons
    for (const [sourceId, state] of sourceStates.entries()) {
      if (state.boatIcon) {
        state.boatIcon.remove();
      }
      removeBoatWindArrow(sourceId);
    }
    sourceStates.clear();
    debug('[LiveMultiBoatLayer] Cleaned up all boat icons');
  });

  return null; // This component doesn't render JSX, it manages D3 overlays
}
